import { Component, Input, OnInit, ChangeDetectorRef, OnChanges, SimpleChanges, Output, EventEmitter, OnDestroy } from '@angular/core';
import { forkJoin, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { ChatbotService } from "../../../services/chatbot-services/chatbot.service";
import { SharedService } from '../../../services/shared-service/shared.service';
import { HttpClient } from '@angular/common/http';
import { LanguageChangeService } from 'src/services/language-change/language-change.service';
import { TranslateService } from '@ngx-translate/core';
import { MetadataConfigService, MetadataFieldConfig } from '../../../services/metadata-config/metadata-config.service';
import { LoggerService } from '../../../services/logger-services/logger.service';
import { RegulationNextService, resolveRegulationVersion } from '../../../services/regulation-next/regulation-next.service';
import { RegulationReferenceMappingService } from '../../../shared/services/regulation-reference-mapping/regulation-reference-mapping.service';
import { environment } from '../../../environments/environment';
import { isRegulationsAgent } from '../../agents/services/regulation-citation/regulation-citation.util';
import {
  MetadataCategoryDisplay,
  resolveMetadataCategoryDisplay,
} from '../../../shared/utils/metadata-category.util';

const COMPACT_LEGACY_INS_ID_REGEX = /^INS\d+$/i;
@Component({
  selector: 'app-chatbot-metadata',
  templateUrl: './chatbot-metadata.component.html',
  styleUrls: ['./chatbot-metadata.component.css']
})
export class ChatbotMetadataComponent implements OnInit, OnChanges, OnDestroy {
  @Input() chatdata: any;
  @Input() configdata: any;
  @Input() tenantId: any;
  @Input() isDocChat: boolean = true;
  @Input() selected_module_name: string = '';
  @Input() agentId: string = '';
  @Input() isloader: boolean = false; // Shows spinner in metadata header when fetching
  @Input() isResponseStreaming: boolean = false; // New input for response streaming state
  @Output() closeHistory = new EventEmitter<void>();
  @Output() DisplaySourcePanel = new EventEmitter<any>();
  metadata: any;
  data: any;
  error: boolean = false;
  errorMessage: string = '';

  localChatData: any;
  localConfigData: any;
  loaderShow: boolean = false;
  InsightViewURL: string = "/regulations?modulePath=/Insights/view-regulation?tab=true&insid=";
  isResponseScroll: boolean = true;
  localTenantId: any;
  currentIndex: number = 0;
  endIndex: number = 0;

  private readonly destroy$ = new Subject<void>();
  private readonly mappedRegulationIds = new Map<string, string>();
  private insIdMappingRequestKey = '';

  /** Error template for CHATBOT METADATA */
  private readonly chatbotMetadataError = (error: any) =>
    `[CHATBOT METADATA] - ${JSON.stringify(error)}`;

  constructor(
    public chatbotService: ChatbotService,
    private readonly sharedScrollService: SharedService,
    private readonly cdr: ChangeDetectorRef,
    private readonly http: HttpClient,
    private readonly languageService: LanguageChangeService,
    private readonly translate: TranslateService,
    private readonly metadataConfigService: MetadataConfigService,
    private readonly loggerService: LoggerService,
    private readonly regulationNextService: RegulationNextService,
    private readonly regulationReferenceMappingService: RegulationReferenceMappingService
  ) { }

  ngOnInit(): void {
    this.localChatData = this.chatdata || {};
    this.localConfigData = this.configdata || {};
    this.localTenantId = this.tenantId || null;
    this.isDocChat = this.isDocChat || true;
    // Don't call updateEndIndex() here as metadata might not be available yet
    this.currentIndex = 0;
    this.endIndex = 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    try {
      if (changes['chatdata'] && this.chatdata) {
        const previousLength = this.localChatData?.metadata?.length || 0;
        this.localChatData = this.chatdata;
        this.insIdMappingRequestKey = '';
        
        // Normalize metadata structure
        this.normalizeMetadata();
              
        // Handle dynamic updates - only reset if it's a new conversation
        if (this.getTotalItems() === 0 || this.getTotalItems() < previousLength) {
          this.currentIndex = 0; // Reset carousel to start for new conversation
        }
        
        // Always update end index when data changes
        this.updateEndIndex();
      }

      if (changes['chatdata'] || changes['isResponseStreaming'] || changes['tenantId']) {
        if (changes['tenantId']) {
          this.localTenantId = this.tenantId || null;
          this.insIdMappingRequestKey = '';
        }
        this.resolveMetadataInsIdMappings();
      }
    } catch (error) {
      this.loggerService.logError(this.chatbotMetadataError(error), "ngOnChanges");
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Check if an item is blocked (contains only blocked: true)
   */
  isItemBlocked(item: any): boolean {
    if (!item || typeof item !== 'object') {
      return false;
    }
    
    // Check if item has blocked property set to true
    if (item.blocked === true) {
      // Check if this item only contains blocked and potentially key/index properties
      const keys = Object.keys(item);
      const allowedKeys = ['blocked', 'key', 'index'];
      const hasOnlyBlockedOrSystemKeys = keys.every(key => allowedKeys.includes(key));
      
      if (hasOnlyBlockedOrSystemKeys) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Normalize metadata to handle both old and new structures
   * Also filters out blocked items
   */
  normalizeMetadata(): void {
    try {
      if (!this.localChatData?.metadata) {
        // Initialize as empty array if metadata is undefined
        if (this.localChatData) {
          this.localChatData.metadata = [];
        }
        return;
      }

      // If metadata is not an array, it might be the old structure with serial keys
      if (!Array.isArray(this.localChatData.metadata)) {
        const metadataObj = this.localChatData.metadata;
        const normalizedArray: any[] = [];
        
        // Convert old structure to array and filter out blocked items
        Object.keys(metadataObj).forEach((key, index) => {
          const item = metadataObj[key];
          if (item && typeof item === 'object') {
            // Add key for backward compatibility
            item.key = key;
            
            // Only add if not blocked
            if (!this.isItemBlocked(item)) {
              normalizedArray.push(item);
            }
          }
        });
        
        // Reassign indices after filtering
        normalizedArray.forEach((item, index) => {
          item.index = index + 1;
        });
        
        this.localChatData.metadata = normalizedArray;
      } else {
        // If it's already an array, add index and filter out blocked items
        const filteredArray: any[] = [];
        let validIndex = 1;
        
        this.localChatData.metadata.forEach((item: any) => {
          if (item && typeof item === 'object' && !this.isItemBlocked(item)) {
            item.index = validIndex++;
            // Don't auto-assign key for new structure - only keep if it exists
            filteredArray.push(item);
          }
        });
        
        this.localChatData.metadata = filteredArray;
      }
    } catch (error) {
      this.loggerService.logError(this.chatbotMetadataError(error), "normalizeMetadata");
    }
  }

  getTotalItems(): number {
    try {
      if (this.localChatData?.metadata && Array.isArray(this.localChatData.metadata)) {
        return this.localChatData.metadata.length;
      }
    } catch (error: any) {
      this.loggerService.logError(this.chatbotMetadataError(error), "getTotalItems")
    }
    return 0;
  }

  updateEndIndex(): void {
    const totalItems = this.getTotalItems();
    if (totalItems === 0) {
      this.endIndex = 0;
    } else {
      this.endIndex = Math.min(this.currentIndex + 3, totalItems);
    }
    this.cdr.detectChanges();
  }

  showLoader() {
    this.loaderShow = true;
  }

  hideLoader() {
    this.loaderShow = false;
  }

  /**
   * Generate view URL for an item
   */
  public getViewUrl(item: any): string | null {
    try {
      // Early validation to prevent infinite loops
      if (!item) {
        return null;
      }
      
      // Check if configdata and FusionAppUIUrl are available
      if (!this.configdata?.FusionAppUIUrl) {
        return null;
      }
      
      const moduleName = this.getModuleName(item);
      
      // Handle null or empty module name to prevent infinite loops
      if (!moduleName) {
        return null;
      }
      
      if (moduleName.toLowerCase() === 'regulations') {
        const regId = this.getRegulationIdForViewUrl(item);
        if (!regId) {
          return null;
        }
        if (this.regulationNextService.hasRegulationNextAccess()) {
          // Version deep-link is regulations-agent only; chatbot v2 keeps the versionless link.
          const version = isRegulationsAgent(this.agentId) ? resolveRegulationVersion(item) : '';
          return this.regulationNextService.buildRegulationNextUrl(
            this.configdata.FusionAppUIUrl,
            regId,
            version
          );
        }
        return this.regulationNextService.buildLegacyRegulationUrl(this.configdata.FusionAppUIUrl, regId);
      } else if (moduleName.toLowerCase() === 'rtq') {
        // For RTQ, prioritize reference_rtq_id, then id
        const rtqId = item.reference_rtq_id || item.id;
        if (!rtqId) {
          return null;
        }
        return this.configdata.FusionAppUIUrl + '/rtq?modulePath=/rtqs/view-rtq&id=' + rtqId;
      }
    } catch (error) {
      this.loggerService.logError(this.chatbotMetadataError(error), "getViewUrl")
      return null;
    }
    return null;
  }

  downloadDocument(documentId: string): void {
    this.showLoader();
    this.chatbotService.DownloadRtqFile(documentId).subscribe({
      next: (data) => {
        if (data.code === 200 && data.data) {
          this.downloadFileFromUrl(data.data, documentId);
        } else {
        }
        this.hideLoader();
      },
      error: (error) => {
        this.loggerService.logError(this.chatbotMetadataError(error), "downloadDocument")
        this.hideLoader();
      }
    });
  }

  downloadFileFromUrl(url: string, documentId: string): void {
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = documentId;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      this.loggerService.logError(this.chatbotMetadataError(error), "downloadFileFromUrl")
    }
  }

  /**
   * Extract countries from both old and new metadata structures
   */
  extractCountries(item: any): string[] {
    try {
      // Handle new metadata structure with 'metadata.countries' array
      if (item.metadata && item.metadata.countries && Array.isArray(item.metadata.countries)) {
        return item.metadata.countries;
      }
      
      // Handle old structure with 'countries' array directly on item
      if (item.countries && Array.isArray(item.countries)) {
        return item.countries;
      }
      
      // Handle old structure with 'identifiers' array
      if (item.identifiers && Array.isArray(item.identifiers)) {
        const countries: string[] = [];
        item.identifiers.forEach((identifier: any) => {
          if (identifier.Country && Array.isArray(identifier.Country)) {
            countries.push(...identifier.Country);
          }
        });
        return countries;
      }
      
      // Handle single country (legacy support)
      if (item.Country && typeof item.Country === 'string') {
        return [item.Country];
      }
    } catch (error) {
      this.loggerService.logError(this.chatbotMetadataError(error), "extractCountries");
      return [];
    }
    
    return [];
  }

  displayCountries(item: any): { firstTwo: string, remainingCount: number } {
    try {
      // For regulations module, use the updated extractCountries method
      const countries = this.extractCountries(item);

      if (!countries || !Array.isArray(countries)) {
        return { firstTwo: '', remainingCount: 0 };
      }

      const countryArray = countries.map(country => country.trim()) || [];
      const firstTwo = countryArray.slice(0, 2).join(', ');
      const remainingCount = Math.max(0, countryArray.length - 2);
      return { firstTwo, remainingCount };
    } catch (error) {
      this.loggerService.logError(this.chatbotMetadataError(error), "displayCountries");
      return { firstTwo: '', remainingCount: 0 };
    }
  }

  /**
   * Resolves the single category label for metadata UI (shared rules).
   */
  getCategoryDisplay(item: unknown): MetadataCategoryDisplay | null {
    return resolveMetadataCategoryDisplay(item);
  }

  /**
   * Extract brand names from both old and new metadata structures
   */
  extractBrandNames(item: any): string[] {
    try {
      // Handle new metadata structure with 'metadata.brandNames' array
      if (item.metadata && item.metadata.brandNames && Array.isArray(item.metadata.brandNames)) {
        return item.metadata.brandNames;
      }
      
      // Handle old structure with 'brandNames' array directly on item
      if (item.brandNames && Array.isArray(item.brandNames)) {
        return item.brandNames;
      }
      
      // Handle single brand name (legacy support)
      if (item.brandName && typeof item.brandName === 'string') {
        return [item.brandName];
      }
      
      // Handle brand name field accessed through metadataConfigService with different possible field names
      const possibleBrandNameFields = ['brandNames', 'brandName', 'metadata.brandNames', 'metadata.brandName'];
      for (const fieldName of possibleBrandNameFields) {
        const brandNameFromField = this.getFieldValue(item, fieldName);
        if (brandNameFromField) {
          if (Array.isArray(brandNameFromField)) {
            return brandNameFromField;
          } else if (typeof brandNameFromField === 'string') {
            return [brandNameFromField];
          }
        }
      }
    } catch (error) {
      this.loggerService.logError(this.chatbotMetadataError(error), "extractBrandNames");
      return [];
    }
    
    return [];
  }

  displayBrandNames(item: any): { firstTwo: string, remainingCount: number } {
    try {
      // Use the extractBrandNames method to handle different data structures
      const brandNames = this.extractBrandNames(item);

      if (!brandNames || !Array.isArray(brandNames)) {
        return { firstTwo: '', remainingCount: 0 };
      }

      const brandNameArray = brandNames.map(brandName => brandName.trim()) || [];
      const firstTwo = brandNameArray.slice(0, 2).join(', ');
      const remainingCount = Math.max(0, brandNameArray.length - 2);
      return { firstTwo, remainingCount };
    } catch (error) {
      this.loggerService.logError(this.chatbotMetadataError(error), "displayBrandNames");
      return { firstTwo: '', remainingCount: 0 };
    }
  }

  prevSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex = Math.max(0, this.currentIndex - 3);
      this.updateEndIndex();
    }
  }

  nextSlide() {
    const totalItems = this.getTotalItems();
    if (this.currentIndex + 3 < totalItems) {
      this.currentIndex += 3;
      this.updateEndIndex();
    }
  }

  viewAllSources() {
    // Close the chat history panel
    this.closeHistory.emit();
    
    // Emit the chat history ID along with the event
    const chatHistoryId = (this.chatdata as any)?.response_id ?? this.chatdata.pk_chathistoryid;
    const sourceData = {
      chatHistoryId: chatHistoryId != null ? String(chatHistoryId) : '',
      domainId: this.localConfigData?.DomainId || '1',
      tenantId: this.localTenantId || '0',
      module_name: this.localConfigData?.module_name
    };    
    this.DisplaySourcePanel.emit(sourceData);
  }

  // Helper method to get relevance class based on relevance_score
  getRelevanceClass(relevanceScore: string): string {
    if (!relevanceScore) return '';
    
    switch (relevanceScore.toLowerCase()) {
      case 'high':
        return 'high';
      case 'medium':
        return 'medium';
      case 'low':
        return 'low';
      default:
        return '';
    }
  }

  // Helper method to check if metadata has relevance scores
  hasRelevanceScores(): boolean {
    
    if (!this.localChatData?.metadata || !Array.isArray(this.localChatData.metadata)) {
      return false;
    }
    
    return this.localChatData.metadata.some((item: any) => item && item.relevance_score);
  }

  // Helper method to get relevance score legend data
  getRelevanceLegend(): any[] {
    if (!this.hasRelevanceScores()) return [];
    
    const scores = new Set<string>();
    
    if (this.localChatData?.metadata && Array.isArray(this.localChatData.metadata)) {
      this.localChatData.metadata.forEach((item: any) => {
        if (item && item.relevance_score) {
          scores.add(item.relevance_score);
        }
      });
    }
    
    const legendItems = [];
    if (scores.has('HIGH')) legendItems.push({ class: 'high', label: this.translate.instant('MOST_RELEVANT') });
    if (scores.has('MEDIUM')) legendItems.push({ class: 'medium', label: this.translate.instant('MEDIUM_RELEVANT') });
    if (scores.has('LOW')) legendItems.push({ class: 'low', label: this.translate.instant('LESS_RELEVANT') });
    
    return legendItems;
  }

  /** Display value for slide range start (1-based); never negative. */
  getSlideStartDisplay(): number {
    return Math.max(1, this.currentIndex + 1);
  }

  /** Display value for slide range end (1-based); never negative. */
  getSlideEndDisplay(): number {
    return Math.max(1, this.endIndex);
  }

  // Get current page items for dynamic rendering
  getCurrentPageItems(): any[] {
    try {
      if (!this.localChatData?.metadata || !Array.isArray(this.localChatData.metadata)) {
        return [];
      }
      
      const totalItems = this.getTotalItems();
      if (totalItems === 0) {
        return [];
      }
      
      // Ensure currentIndex is within bounds (never negative, never past last page)
      if (this.currentIndex < 0) {
        this.currentIndex = 0;
      }
      if (this.currentIndex >= totalItems) {
        this.currentIndex = Math.max(0, totalItems - 3);
      }
      
      // Ensure endIndex is properly calculated
      this.endIndex = Math.min(this.currentIndex + 3, totalItems);
      
      return this.localChatData.metadata.slice(this.currentIndex, this.endIndex);
    } catch (error) {
      this.loggerService.logError(this.chatbotMetadataError(error), "getCurrentPageItems");
      return [];
    }
  }

  // Auto-scroll to show new items as they arrive
  autoScrollToNewItems(): void {
    const totalItems = this.getTotalItems();
    if (totalItems > 0) {
      // If we have new items beyond current view, scroll to show them
      if (this.currentIndex + 3 < totalItems) {
        this.currentIndex = Math.max(0, totalItems - 3);
      }
      this.updateEndIndex();
    }
  }

  /**
   * Get module name from metadata item
   */
  getModuleName(item: any): string | null {
    // First try to get module_name directly from the item (new API structure)
    if (item?.['module_name']) {
      return item['module_name'];
    }
    
    // Map selected_module_name to our configuration module names
    if (this.selected_module_name === 'view_access_to_regulations') {
      return 'regulations';
    } else if (this.selected_module_name === 'view_access_to_rtq') {
      return 'rtq';
    }
    
    // If no mapping found, return null (will use legacy rendering)
    return null;
  }

  /**
   * Get field value using the configuration service
   */
  getFieldValue(item: any, fieldKey: string): any {
    return this.metadataConfigService.getFieldValue(item, fieldKey);
  }

  /**
   * Check if field has value
   */
  hasFieldValue(item: any, fieldKey: string): boolean {
    return this.metadataConfigService.hasFieldValue(item, fieldKey);
  }

  /**
   * Get formatted field value
   */
  getFormattedFieldValue(item: any, field: MetadataFieldConfig): any {
    const value = this.getFieldValue(item, field.key);
    return this.metadataConfigService.formatFieldValueEnhanced(value, field);
  }

  /**
   * Get visible fields for compact view based on module
   */
  getCompactFields(item: any): MetadataFieldConfig[] {
    const moduleName = this.getModuleName(item);
    return this.metadataConfigService.discoverAvailableFields(item, 'compact', moduleName || undefined);
  }

  /**
   * Get visible fields for detailed view based on module
   */
  getDetailedFields(item: any): MetadataFieldConfig[] {
    const moduleName = this.getModuleName(item);
    return this.metadataConfigService.discoverAvailableFields(item, 'detailed', moduleName || undefined);
  }

  /**
   * Get title for display (backwards compatible)
   */
  getDisplayTitle(item: any): string {
    try {
      // Check multiple possible title fields in order of preference
      const titleFields = [
        'metadata.title',
        'title', 
        'metadata.query_title',
        'query_title'
      ];
      
      for (const field of titleFields) {
        const value = this.getFieldValue(item, field);
        if (value && value.trim()) {
          return value;
        }
      }
    } catch (error) {
      this.loggerService.logError(this.chatbotMetadataError(error), "getDisplayTitle");
    }
    return '';
  }

  /**
   * Title truncated to maxLen with ellipsis when longer (same as legacy path: 40 chars + '...').
   * Used so universal and legacy metadata rendering match in chatbot and agent.
   */
  getDisplayTitleTruncated(item: any, maxLen: number = 40): string {
    const title = this.getDisplayTitle(item);
    if (!title) return '';
    if (title.length <= maxLen) return title;
    return title.slice(0, maxLen) + '...';
  }

  /**
   * Get ID for display (backwards compatible)
   */
  getDisplayId(item: any): string {
    try {
      // Check multiple possible ID fields in order of preference
      const idFields = [
        'id',
        'ins_id',
        'rtq_id',
        'metadata.encrypted_regulation_id',
        'metadata.reference_insight_id'
      ];
      
      for (const field of idFields) {
        const value = this.getFieldValue(item, field);
        if (value && value.trim()) {
          return value;
        }
      }
    } catch (error) {
      this.loggerService.logError(this.chatbotMetadataError(error), "getDisplayId");
    }
    return '';
  }

  /**
   * Regulation ID used in regulation-details URLs.
   * For silo tenants, compact legacy IDs (e.g. INS17340) are mapped to tenant-scoped IDs via API.
   */
  private getRegulationIdForViewUrl(item: any): string {
    const referenceId = this.readTrimmedFieldValue(
      item,
      'metadata.reference_insight_id',
      'reference_insight_id'
    );
    const insId = this.readTrimmedFieldValue(item, 'ins_id');
    const itemId = this.readTrimmedFieldValue(item, 'id');

    if (referenceId && !this.isCompactLegacyInsId(referenceId)) {
      return referenceId;
    }

    const legacyId = referenceId || insId || itemId;
    if (!legacyId) {
      return '';
    }

    if (!this.isCompactLegacyInsId(legacyId) || !this.shouldResolveInsIdMappings()) {
      return legacyId;
    }

    const normalizedLegacyId = legacyId.toUpperCase();
    const tenantId = this.getTenantId();
    return (
      this.mappedRegulationIds.get(normalizedLegacyId)
      || this.regulationReferenceMappingService.getCachedMappedRegulationId(normalizedLegacyId, tenantId)
      || legacyId
    );
  }

  private resolveMetadataInsIdMappings(): void {
    if (!this.shouldResolveInsIdMappings() || !Array.isArray(this.localChatData?.metadata)) {
      return;
    }

    const tenantId = this.getTenantId();
    const legacyIds = new Set<string>();

    this.localChatData.metadata.forEach((item: any) => {
      if (this.getModuleName(item)?.toLowerCase() !== 'regulations') {
        return;
      }

      const referenceId = this.readTrimmedFieldValue(
        item,
        'metadata.reference_insight_id',
        'reference_insight_id'
      );
      const legacyId = referenceId
        || this.readTrimmedFieldValue(item, 'ins_id')
        || this.readTrimmedFieldValue(item, 'id');
      if (legacyId && this.isCompactLegacyInsId(legacyId)) {
        legacyIds.add(legacyId.toUpperCase());
      }
    });

    if (legacyIds.size === 0) {
      return;
    }

    const requestKey = `${tenantId}|${Array.from(legacyIds).sort((a, b) => a.localeCompare(b)).join(',')}`;
    if (requestKey === this.insIdMappingRequestKey) {
      return;
    }
    this.insIdMappingRequestKey = requestKey;

    forkJoin(
      Array.from(legacyIds).map((insId) =>
        this.regulationReferenceMappingService.getMappedRegulationId(insId, insId, tenantId).pipe(
          map((mappedId) => [insId, (mappedId || insId).toUpperCase()] as const)
        )
      )
    )
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (entries) => {
          entries.forEach(([originalId, mappedId]) => {
            this.mappedRegulationIds.set(originalId, mappedId);
          });
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.loggerService.logError(this.chatbotMetadataError(error), 'resolveMetadataInsIdMappings');
        },
      });
  }

  private shouldResolveInsIdMappings(): boolean {
    const tenantId = this.getTenantId();
    return !this.isResponseStreaming && !!tenantId && tenantId !== '0';
  }

  private getTenantId(): string {
    return String(this.localTenantId || environment.TenantId || '').trim();
  }

  private isCompactLegacyInsId(insId: string): boolean {
    return COMPACT_LEGACY_INS_ID_REGEX.test(insId.trim());
  }

  private readTrimmedFieldValue(item: any, ...fieldKeys: string[]): string {
    for (const fieldKey of fieldKeys) {
      const value = this.getFieldValue(item, fieldKey);
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return '';
  }
}